import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@speaking-coach/ui'
import { FilterTh, describeColumnFilters } from '../common/column-filter-indicator'
/**
 * Operator activity (PROV-AUDIT).
 *
 * Defaults to MY ACTIVITY — the signed-in operator's own audited
 * actions — and the Operator filter pivots to a colleague's feed.
 * That peer inspection is the card's EXPLICIT authorization rule (the
 * flat admin role grants inspecting any OPERATOR's provenance), and
 * the server bounds it: `actorId` accepts admin identities only, so
 * the feed stays operator accountability rather than a general
 * browse-a-user's-actions console. Self-service actors appear on
 * entity timelines, where attribution belongs.
 *
 * The same page also renders one entity's full history when pivoted
 * via URL (`?entityType=…&entityId=…`). Every pivot and filter lives
 * in the URL so a view is shareable and survives a refresh, with the
 * feed's identity announced in the toolbar tier.
 *
 * There is no free-text search on purpose: the endpoint offers none
 * (searching seven years of JSONB payloads per keystroke is the query
 * shape this surface refuses), and a search box that silently filters
 * one fetched page would be worse than none.
 */
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { queryKeys } from '../api/query-keys.js'
import {
  ClearFiltersButton,
  FilterField,
  FilteredEmptyState,
  ListPage,
  ListPagination,
} from '../common/list-page.js'
import { TABLE, TABLE_HEAD_ROW, TABLE_ROW, TABLE_WRAP } from '../common/table.js'
import { useResetOnChange } from '../common/use-reset-on-change.js'
import { ClientPicker } from '../clients/client-picker.js'
import { DateFilterInput } from '../common/date-filter-input'
import { useAtomValue } from 'jotai'
import { coooriAdminsApi } from '../admins/api.js'
import { adminUserAtom } from '../state/atoms.js'
import { activityApi, messageForActivityError } from './api.js'
import {
  ActivityActorCell,
  ActivityChangeSummary,
  ActivityEntityLinks,
  ActivityWhen,
  ENTITY_TYPE_LABELS,
  FAMILY_LABELS,
  OPERATION_LABELS,
  useNow,
  jstDayEnd,
  jstDayStart,
} from './activity-presentation.js'
import type {
  ActivityEntityType,
  ActivityFamily,
  ActivityOperation,
  EntityActivityParams,
  MyActivityParams,
} from './types.js'

const PAGE_SIZE = 25
const ALL = '__all__'
const ME = '__me__'

const FAMILY_VALUES = Object.keys(FAMILY_LABELS) as ActivityFamily[]
const ENTITY_TYPE_VALUES = Object.keys(ENTITY_TYPE_LABELS) as ActivityEntityType[]
const OPERATION_VALUES = Object.keys(OPERATION_LABELS) as ActivityOperation[]

function asFamily(value: string | null): ActivityFamily | undefined {
  return FAMILY_VALUES.includes(value as ActivityFamily)
    ? (value as ActivityFamily)
    : undefined
}

function asEntityType(value: string | null): ActivityEntityType | undefined {
  return ENTITY_TYPE_VALUES.includes(value as ActivityEntityType)
    ? (value as ActivityEntityType)
    : undefined
}

function asOperation(value: string | null): ActivityOperation | undefined {
  return OPERATION_VALUES.includes(value as ActivityOperation)
    ? (value as ActivityOperation)
    : undefined
}

export function ActivityPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = useState(0)
  // One clock per table: the relative "When" column keeps telling the
  // truth while the page sits open.
  const now = useNow()

  // The entity pivot: set by every detail page's "View all activity".
  const pivotEntityType = asEntityType(searchParams.get('entityType'))
  const pivotEntityId = searchParams.get('entityId') ?? ''
  const pivotClientId = searchParams.get('clientId') ?? ''
  const entityMode = pivotEntityType !== undefined && pivotEntityId !== ''

  const family = asFamily(searchParams.get('family'))
  const operation = asOperation(searchParams.get('operation'))
  const subjectType = entityMode ? undefined : asEntityType(searchParams.get('subject'))
  const filterClientId = entityMode ? '' : pivotClientId
  const sinceDay = searchParams.get('since') ?? ''
  const untilDay = searchParams.get('until') ?? ''
  // A colleague's feed instead of the caller's own — "Me" is the
  // default, so the param only exists while inspecting someone else.
  const actorId = entityMode ? '' : (searchParams.get('actor') ?? '')

  // The operator population is small (Cooori staff), so a plain Select
  // over the directory works where clients needed a server-searched
  // combobox. Fetched only for the operator feed's filter rail.
  const adminsQuery = useQuery({
    queryKey: queryKeys.coooriAdminList({}),
    queryFn: () => coooriAdminsApi.list({}),
    enabled: !entityMode,
  })
  const currentAdmin = useAtomValue(adminUserAtom)
  const colleagues = (adminsQuery.data?.admins ?? []).filter(
    (admin) => admin.userId !== currentAdmin?.id,
  )
  const actorLabel =
    actorId === ''
      ? null
      : (adminsQuery.data?.admins.find((admin) => admin.userId === actorId)?.name ??
        `Operator ${actorId.slice(0, 8)}…`)

  function updateParams(mutate: (params: URLSearchParams) => void): void {
    const next = new URLSearchParams(searchParams)
    mutate(next)
    setSearchParams(next, { replace: true })
  }

  // One joined key: `useResetOnChange` compares with Object.is, so an
  // array literal would reset on every render.
  const filterKey = [
    family ?? '',
    operation ?? '',
    subjectType ?? '',
    filterClientId,
    sinceDay,
    untilDay,
    actorId,
    pivotEntityType ?? '',
    pivotEntityId,
  ].join('|')
  useResetOnChange(filterKey, () => setPage(0))

  const mineParams: MyActivityParams = {
    actorId: actorId === '' ? undefined : actorId,
    family,
    operation,
    entityType: subjectType,
    clientId: filterClientId === '' ? undefined : filterClientId,
    since: sinceDay === '' ? undefined : jstDayStart(sinceDay),
    until: untilDay === '' ? undefined : jstDayEnd(untilDay),
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  }
  const entityParams: EntityActivityParams = {
    entityType: pivotEntityType ?? 'client',
    entityId: pivotEntityId,
    clientId: pivotClientId === '' ? undefined : pivotClientId,
    family,
    operation,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  }

  // Two declared queries with one enabled, rather than one query whose
  // key CHANGES SHAPE — the union defeats useQuery's typing, and only
  // the active mode should ever fetch.
  const entityFeed = useQuery({
    queryKey: queryKeys.activityEntity(entityParams),
    queryFn: () => activityApi.entity(entityParams),
    enabled: entityMode,
  })
  const mineFeed = useQuery({
    queryKey: queryKeys.activityMine(mineParams),
    queryFn: () => activityApi.mine(mineParams),
    enabled: !entityMode,
  })
  const feed = entityMode ? entityFeed : mineFeed

  const entries = feed.data?.entries ?? []
  const hasFilters =
    family !== undefined ||
    operation !== undefined ||
    subjectType !== undefined ||
    filterClientId !== '' ||
    sinceDay !== '' ||
    untilDay !== '' ||
    actorId !== ''
  const activeFilterCount = [
    family !== undefined,
    operation !== undefined,
    subjectType !== undefined,
    filterClientId !== '',
    sinceDay !== '' || untilDay !== '',
    actorId !== '',
  ].filter(Boolean).length

  function clearFilters(): void {
    updateParams((params) => {
      params.delete('family')
      params.delete('operation')
      params.delete('subject')
      params.delete('since')
      params.delete('until')
      params.delete('actor')
      if (!entityMode) params.delete('clientId')
    })
  }

  // The subject entity the pivoted timeline is about, for the banner.
  // The caller page passes the display name it already holds
  // (`entityLabel`) so the banner needs no lookup; display-only, so a
  // hand-edited URL can at worst mislabel its own view.
  const pivotName = searchParams.get('entityLabel') ?? ''
  const pivotLabel =
    entityMode && pivotEntityType !== undefined
      ? pivotName !== ''
        ? `${ENTITY_TYPE_LABELS[pivotEntityType]} ${pivotName}`
        : `${ENTITY_TYPE_LABELS[pivotEntityType]} ${pivotEntityId.slice(0, 8)}…`
      : ''

  return (
    <ListPage
      title="Activity"
      filterAction={
        <ClearFiltersButton hasFilters={hasFilters} onClear={clearFilters} />
      }
      filters={
        <>
          <FilterField active={family !== undefined}
            label="Kind of change"
            htmlFor="activity-family"
            help="Invitations, access changes, setup edits…"
          >
            <Select
              value={family ?? ALL}
              onValueChange={(value) =>
                updateParams((params) => {
                  if (value === ALL) params.delete('family')
                  else params.set('family', value)
                })
              }
            >
              <SelectTrigger id="activity-family" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All families</SelectItem>
                {FAMILY_VALUES.map((value) => (
                  <SelectItem key={value} value={value}>
                    {FAMILY_LABELS[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
          <FilterField active={operation !== undefined}
            label="Operation"
            htmlFor="activity-operation"
            help="Removed = deletions, deactivations, endings."
          >
            <Select
              value={operation ?? ALL}
              onValueChange={(value) =>
                updateParams((params) => {
                  if (value === ALL) params.delete('operation')
                  else params.set('operation', value)
                })
              }
            >
              <SelectTrigger id="activity-operation" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Any operation</SelectItem>
                {OPERATION_VALUES.map((value) => (
                  <SelectItem key={value} value={value}>
                    {OPERATION_LABELS[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
          {!entityMode && (
            <>
              {/* Held (not disabled) until the directory loads: a Select
                  whose options have not arrived misreports its own value,
                  and a failed directory fetch must not break the page —
                  the URL-backed choice still applies to the request. */}
              {adminsQuery.isSuccess && (
                <FilterField active={actorId !== ''}
                  label="Operator"
                  htmlFor="activity-actor"
                  help="Whose audited actions to show."
                >
                  <Select
                    value={actorId === '' ? ME : actorId}
                    onValueChange={(value) =>
                      updateParams((params) => {
                        if (value === ME) params.delete('actor')
                        else params.set('actor', value)
                      })
                    }
                  >
                    <SelectTrigger id="activity-actor" size="sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ME}>Me</SelectItem>
                      {colleagues.map((admin) => (
                        <SelectItem key={admin.userId} value={admin.userId}>
                          {admin.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FilterField>
              )}
              <FilterField active={subjectType !== undefined}
                label="About"
                htmlFor="activity-subject"
                help="The kind of thing the action was about."
              >
                <Select
                  value={subjectType ?? ALL}
                  onValueChange={(value) =>
                    updateParams((params) => {
                      if (value === ALL) params.delete('subject')
                      else params.set('subject', value)
                    })
                  }
                >
                  <SelectTrigger id="activity-subject" size="sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All entities</SelectItem>
                    {ENTITY_TYPE_VALUES.map((value) => (
                      <SelectItem key={value} value={value}>
                        {ENTITY_TYPE_LABELS[value]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterField>
              <FilterField active={filterClientId !== ''} label="Client" htmlFor="activity-client">
                <ClientPicker
                  id="activity-client"
                  label="Client"
                  value={filterClientId}
                  emptyLabel="All clients"
                  size="sm"
                  showDetails={false}
                  onChange={(clientId) =>
                    updateParams((params) => {
                      if (clientId === '') params.delete('clientId')
                      else params.set('clientId', clientId)
                    })
                  }
                />
              </FilterField>
              <FilterField active={sinceDay !== ''}
                label="From"
                htmlFor="activity-since"
                help="JST days, inclusive."
              >
                <DateFilterInput
                  id="activity-since"
                  label="From"
                  size="sm"
                  value={sinceDay}
                  onValueChange={(value) =>
                    updateParams((params) => {
                      if (value === '') params.delete('since')
                      else params.set('since', value)
                    })
                  }
                />
              </FilterField>
              <FilterField active={untilDay !== ''} label="To" htmlFor="activity-until">
                <DateFilterInput
                  id="activity-until"
                  label="To"
                  size="sm"
                  value={untilDay}
                  onValueChange={(value) =>
                    updateParams((params) => {
                      if (value === '') params.delete('until')
                      else params.set('until', value)
                    })
                  }
                />
              </FilterField>
            </>
          )}
        </>
      }
      search={
        /* The feed's identity rides the toolbar tier where search would
           sit (there is no text search over audit payloads): whose
           events these are stays on screen while a long table scrolls,
           and the table top aligns with the rail like every other list. */
        entityMode ? (
          <p className="truncate text-body text-text-secondary">
            Showing all activity for{' '}
            <span className="font-medium text-text-primary">{pivotLabel}</span>
            {' · '}
            <Link to="/admin/activity" className="underline whitespace-nowrap">
              Back to my activity
            </Link>
          </p>
        ) : actorId !== '' ? (
          <p className="truncate text-body text-text-secondary">
            Audited actions by{' '}
            <span className="font-medium text-text-primary">{actorLabel}</span>, newest
            first.
          </p>
        ) : (
          <p className="truncate text-body text-text-secondary">
            Your own audited actions, newest first.
          </p>
        )
      }
      activeFilterCount={activeFilterCount}
      pagination={
        feed.isSuccess && entries.length > 0 ? (
          <ListPagination
            summary={
              <>
                Showing events {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + entries.length}
              </>
            }
            previousDisabled={page === 0}
            nextDisabled={feed.data?.hasMore !== true}
            onPrevious={() => setPage((value) => value - 1)}
            onNext={() => setPage((value) => value + 1)}
          />
        ) : undefined
      }
    >
      {feed.isPending ? (
        <p className="text-text-secondary text-body">Loading activity…</p>
      ) : feed.isError ? (
        <div role="alert" className="text-body text-danger-text">
          {messageForActivityError(feed.error)}
        </div>
      ) : entries.length === 0 ? (
        <FilteredEmptyState
          hasFilters={hasFilters}
          noun="events"
          onClear={clearFilters}
          emptyHint={
            entityMode
              ? 'No provisioning activity recorded for this entity yet.'
              : actorId !== ''
                ? 'No audited provisioning actions for this operator yet.'
                : 'Nothing audited under your name yet — provisioning actions you take will appear here.'
          }
        />
      ) : (
        <div className={TABLE_WRAP}>
          <table className={TABLE}>
            <thead>
              <tr className={TABLE_HEAD_ROW}>
                <FilterTh className="p-3 font-medium" label="When" filterDescription={describeColumnFilters(sinceDay && `On or after ${sinceDay}`, untilDay && `On or before ${untilDay}`)} />
                <FilterTh className="p-3 font-medium" label="What" filterDescription={describeColumnFilters(family && `Kind: ${family}`, operation && `Operation: ${operation}`, subjectType && `Subject: ${subjectType}`, entityMode && `Entity: ${pivotName || pivotEntityId}`)} />
                <FilterTh className="p-3 font-medium" label="Who" filterDescription={actorLabel ? `Operator: ${actorLabel}` : undefined} />
                <FilterTh className="p-3 font-medium" label="Client" filterDescription={filterClientId ? 'Selected client' : undefined} />
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id} className={TABLE_ROW}>
                  <td className="p-3 align-top whitespace-nowrap">
                    <ActivityWhen occurredAt={entry.occurredAt} now={now} />
                  </td>
                  <td className="p-3 align-top">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span>{entry.label}</span>
                      <ActivityEntityLinks
                        entities={entry.entities}
                        skipEntityId={entityMode ? pivotEntityId : undefined}
                      />
                    </div>
                    <ActivityChangeSummary entry={entry} />
                  </td>
                  <td className="p-3 align-top">
                    <ActivityActorCell entry={entry} />
                  </td>
                  <td className="p-3 align-top text-text-secondary">
                    {entry.clientId !== null ? (
                      <Link to={`/admin/clients/${entry.clientId}`} className="underline">
                        {entry.clientLabel ?? `${entry.clientId.slice(0, 8)}…`}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ListPage>
  )
}
